/**
 * Local-only persistence of the user's drag-reorder preference for the
 * Decide › Balance list. Lives in `localStorage` because:
 *
 *   1. Priority is a *visual* preference for the BalanceList — it does
 *      NOT affect scheduling, cap enforcement, or any backend behavior.
 *      (Design decision 2026-05-06: scheduler reads commit time + gap;
 *      priority is purely "what order do I want to scan this list in".)
 *
 *   2. Storing it locally avoids a migration + API + cross-team coupling
 *      for a UX preference. If the user wipes their browser or switches
 *      device, the list falls back to the default sort (gap descending).
 *
 *   3. Per-user scoping prevents two people sharing the same browser
 *      from leaking each other's list ordering.
 *
 * If a future requirement demands cross-device sync OR scheduler
 * influence, escalate to a backend column rather than expanding this
 * file. See conversation thread 2026-05-06 for the rationale.
 */

const STORAGE_PREFIX = "decisionease:balance-list-priority";

export type AreaPriorityMap = Record<string, number>;

function buildKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_PREFIX}:${userId}`;
}

function safeParse(raw: string | null): AreaPriorityMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out: AreaPriorityMap = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "number" && Number.isFinite(v)) {
          out[k] = v;
        }
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

export function loadAreaPriority(
  userId: string | null | undefined,
): AreaPriorityMap {
  if (typeof window === "undefined") return {};
  const key = buildKey(userId);
  if (!key) return {};
  return safeParse(window.localStorage.getItem(key));
}

export function saveAreaPriority(
  userId: string | null | undefined,
  map: AreaPriorityMap,
): void {
  if (typeof window === "undefined") return;
  const key = buildKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Quota exceeded / private mode — silently ignore. The default sort
    // (gap descending) still works without persistence.
  }
}

/**
 * Compare two area ids by their persisted priority. Areas not in the
 * map sort to the bottom (treated as priority `Infinity`). Same priority
 * falls back to alphabetical on the id string for deterministic order.
 */
export function comparePriority(
  map: AreaPriorityMap,
  aId: string,
  bId: string,
): number {
  const ap = map[aId];
  const bp = map[bId];
  const a = ap == null ? Number.POSITIVE_INFINITY : ap;
  const b = bp == null ? Number.POSITIVE_INFINITY : bp;
  if (a !== b) return a - b;
  return aId.localeCompare(bId);
}

/**
 * Reassign priority indices to match `orderedIds` exactly. Areas not in
 * the supplied list are dropped from the map (no longer pinned).
 */
export function reindexPriority(orderedIds: string[]): AreaPriorityMap {
  const next: AreaPriorityMap = {};
  orderedIds.forEach((id, i) => {
    next[id] = i + 1;
  });
  return next;
}
